import {
    useState,
    useRef,
    useMemo
} from 'react'
import { useUser } from '@shared/hooks/user'
import { useTheme } from '@mui/material/styles'
import { useDialogContext } from '@shared/hooks/dialogContext'

import Card from '@mui/material/Card'
import CardActionArea from '@mui/material/CardActionArea'
import CardContent from '@mui/material/CardContent'
import CardMedia from '@mui/material/CardMedia'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Masonry from 'react-masonry-css'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Doc from './Doc'
import { docs } from './data'

export default function Docs() {
    const {
        form: { setValue, getValues },
        urls,
        viewFields,
    } = useDialogContext()
    const [doc, setDoc] = useState(null)
    const [generating, setGenerating] = useState(false)
    const docRef = useRef(null)
    const theme = useTheme()
    const gap = theme.spacing(2)
    const user = useUser()

    const resolvedDocs = useMemo(() => docs({
        contractor: getValues('contractor'),
        user: `${user.first_name} ${user.last_name}`,
        user_signature: user.signature,
        supervisor_signature: getValues('supervisor_signature'),
    }), [getValues, user])

    const handleSelection = (d) => setDoc(d)

    const handleAdd = async () => {
        if (!docRef.current) return
        setGenerating(true)
        try {
            const blob = await docRef.current.generate()
            const fileName = `${doc.text}-${Date.now()}.png`
            const file = new File([blob], fileName, { type: 'image/png' })
            const url = URL.createObjectURL(blob)

            urls.current = [...urls.current, url]

            const prevImages = getValues('images') || []
            setValue('images', [
                ...prevImages,
                {
                    id: crypto.randomUUID(),
                    file,
                    url,
                    dep: 'documents'
                }
            ], {
                shouldDirty: true,
                shouldValidate: true,
            })

            setDoc(null)
        } finally {
            setGenerating(false)
        }
    }

    return (
        <>
            <DialogTitle>
                {!doc ? 'النماذج' : doc.text}
                <Typography
                    sx={{display: 'block'}}
                    variant='caption'
                >ملاحظة: يمكنك عمل zoom في الصورة.</Typography>
            </DialogTitle>
            <DialogContent>
                {!doc
                    ? (
                        <div style={{ height: '100%' }}>
                            <Box
                                sx={{
                                    '& .my-masonry-grid': {
                                        display: 'flex',
                                        marginLeft: `-${gap}`,
                                        width: 'auto',
                                    },
                                    '& .my-masonry-grid_column': {
                                        paddingLeft: gap,
                                        backgroundClip: 'padding-box',
                                    },
                                    '& .my-masonry-grid_column > div': {
                                        marginBottom: gap,
                                    },
                                }}
                            >
                                <Masonry
                                    breakpointCols={2}
                                    className='my-masonry-grid'
                                    columnClassName='my-masonry-grid_column'
                                >
                                    {resolvedDocs.map((d) => (
                                        <Card key={d.text} variant='outlined'>
                                            <CardActionArea onClick={() => handleSelection(d)}>
                                                <CardMedia component='img' image={d.image} alt={d.text} />
                                                <CardContent>
                                                    <Typography>{d.text}</Typography>
                                                </CardContent>
                                            </CardActionArea>
                                        </Card>
                                    ))}
                                </Masonry>
                            </Box>
                        </div>
                    ) : (
                        <Doc ref={docRef} doc={doc} />
                    )
                }
            </DialogContent>
            <DialogActions>
                {!doc
                    ? (
                        <Button variant='text' onClick={viewFields}>رجوع</Button>
                    ) : (
                        <>
                            <Button variant='text' onClick={() => setDoc(null)} disabled={generating}>رجوع</Button>
                            <Button variant='text' onClick={handleAdd} disabled={generating}>
                                {generating ? 'جارِ الإنشاء...' : 'إضافة'}
                            </Button>
                        </>
                    )
                }
            </DialogActions>
        </>
    )
}